/**
 * "Add to Home Screen" — one declaration of the guide, used by every surface.
 *
 * The app bundle already had two React guides (src/web/src/design-system/
 * components/IosInstallGuide + AndroidInstallGuide), but they are written in
 * Tailwind v4 classes, and the two surfaces that now need the same affordance
 * cannot use them: the marketing homepage is Tailwind v3 + plain CSS, and the
 * story page (/keep/:token) is a self-contained server-rendered document with
 * no bundle at all. So the steps, the copy and the skin live here as plain
 * strings — importable from Node (the edge templates) and from either Vite
 * bundle (AddToHomeScreen.tsx) — rather than being typed out a third and
 * fourth time and drifting apart.
 *
 * The flows reproduce what the browsers actually do, and match the app
 * bundle's guides taps-for-taps:
 *   iOS Safari:      ••• (More) → Share → Add to Home Screen
 *   Chrome Android:  ⋮ (More) → Add to Home screen → Install
 * iOS exposes no install API at all; Chromium exposes `beforeinstallprompt`,
 * which callers fire first and only fall back to this guide when it is absent
 * (already dismissed, already installed, or a non-Chromium browser).
 */

/** The stash `beforeinstallprompt` is parked on — one name across all surfaces. */
export const INSTALL_PROMPT_KEY = '__oriInstallPrompt';

// Icons. Inline SVG because these render inside a server-built document with no
// icon library — the same glyphs the app bundle's guides pull from lucide.
const SVG = {
  close: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"/></svg>',
  phone: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="6" y="2" width="12" height="20" rx="2.5"/><path d="M11 18h2"/></svg>',
  // The iOS share glyph (box with an up arrow) — no icon set has an exact match.
  iosShare: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 3v12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><path d="m8.5 6.5 3.5-3.5 3.5 3.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M7 10H5.5A1.5 1.5 0 0 0 4 11.5v7A1.5 1.5 0 0 0 5.5 20h13a1.5 1.5 0 0 0 1.5-1.5v-7A1.5 1.5 0 0 0 18.5 10H17" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>',
  squarePlus: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="4"/><path d="M12 8v8M8 12h8"/></svg>',
  bookmark: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 4h12v17l-6-4-6 4Z"/></svg>',
  book: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 5a2 2 0 0 1 2-2h5v18H6a2 2 0 0 1-2-2Z"/><path d="M20 5a2 2 0 0 0-2-2h-5v18h5a2 2 0 0 0 2-2Z"/></svg>',
  star: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m12 3 2.7 5.6 6.1.9-4.4 4.3 1 6.1-5.4-2.9-5.4 2.9 1-6.1L3.2 9.5l6.1-.9Z"/></svg>',
  search: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>',
  plus: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>',
  share2: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><path d="m8.6 13.5 6.8 4M15.4 6.5l-6.8 4"/></svg>',
  history: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 12a9 9 0 1 0 2.6-6.4"/><path d="M3 4v4h4"/><path d="M12 7v5l3.5 2"/></svg>',
  download: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 4v10"/><path d="m8 10.5 4 3.5 4-3.5"/><path d="M5 19h14"/></svg>',
  dotsH: '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="5.5" cy="12" r="1.6"/><circle cx="12" cy="12" r="1.6"/><circle cx="18.5" cy="12" r="1.6"/></svg>',
  dotsV: '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="12" cy="5.5" r="1.6"/><circle cx="12" cy="12" r="1.6"/><circle cx="12" cy="18.5" r="1.6"/></svg>',
  chevronLeft: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m14 6-6 6 6 6"/></svg>',
  lines: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><path d="M4 7h16M4 12h11M4 17h16"/></svg>',
  reload: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 12a8 8 0 1 1-2.3-5.6"/><path d="M20 4v4h-4"/></svg>',
};

/** The trigger button's own icon, for callers that render their own button.
 *  A download mark — arrow settling into a rounded tray (拍板 2026-08-23,
 *  replacing the phone outline). Deliberately NOT SVG.download: that one is
 *  the guide's mock of the browsers' own menu rows and must keep matching
 *  what the OS actually draws. */
export const INSTALL_BUTTON_ICON_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 4v9"/><path d="m8 9.5 4 3.5 4-3.5"/><path d="M4 14v2.5A3.5 3.5 0 0 0 7.5 20h9a3.5 3.5 0 0 0 3.5-3.5V14"/></svg>';

function escapeHtml(value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

// One menu / share-sheet row: icon LEFT, label RIGHT, as both real OSes draw
// them. `active` is the row to tap — a teaching cue (a clean white row on the
// gray sheet, which is also iOS's own pressed-row look), not real chrome.
function row(label, icon, active) {
  return `<div class="a2hs-row${active ? ' is-active' : ''}"><span class="a2hs-row-ico">${icon}</span><span class="a2hs-row-label">${escapeHtml(label)}</span></div>`;
}

function step(n, caption, mock, { last = false } = {}) {
  return `<div class="a2hs-step">
      <div class="a2hs-rail"><span class="a2hs-badge">${n}</span>${last ? '' : '<span class="a2hs-line"></span>'}</div>
      <div class="a2hs-step-body">
        <p class="a2hs-cap">${caption}</p>
        ${mock ? `<div class="a2hs-mock">${mock}</div>` : ''}
      </div>
    </div>`;
}

// The app row both flows end on: the icon and name the OS is about to save.
function appRow(host, sub) {
  return `<div class="a2hs-app-row">
        <img class="a2hs-app-thumb" src="/icon-192.png" alt="" decoding="async">
        <span class="a2hs-app-text"><b>Ember</b><i>${escapeHtml(sub || host)}</i></span>
      </div>`;
}

function iosSteps(host) {
  return [
    step(1, 'Tap the ••• (More) button at the bottom-right.', `<div class="a2hs-bar">
        <span class="a2hs-bar-ico">${SVG.chevronLeft}</span>
        <span class="a2hs-bar-ico">${SVG.lines}</span>
        <span class="a2hs-url">${escapeHtml(host)}<i>${SVG.reload}</i></span>
        <span class="a2hs-tap a2hs-tap-ios">${SVG.dotsH}</span>
      </div>`),
    step(2, 'Tap Share.', `<div class="a2hs-sheet">
        ${row('Share', SVG.iosShare, true)}
        ${row('Add to Bookmarks', SVG.bookmark, false)}
        ${row('Add Bookmark to…', SVG.book, false)}
      </div>`),
    step(3, 'Scroll down and tap Add to Home Screen, then Add.', `<div class="a2hs-sheet">
        ${appRow(host, `${host}/me`)}
        ${row('Add to Favorites', SVG.star, false)}
        ${row('Find on Page', SVG.search, false)}
        ${row('Add to Home Screen', SVG.squarePlus, true)}
      </div>`, { last: true }),
  ].join('\n');
}

function androidSteps(host) {
  return [
    step(1, 'Tap the ⋮ (More) button at the top-right.', `<div class="a2hs-bar">
        <span class="a2hs-url a2hs-url-wide">${escapeHtml(host)}</span>
        <span class="a2hs-tap a2hs-tap-android">${SVG.dotsV}</span>
      </div>`),
    step(2, 'Tap Add to Home screen (or Install app).', `<div class="a2hs-sheet">
        ${row('New tab', SVG.plus, false)}
        ${row('Share…', SVG.share2, false)}
        ${row('History', SVG.history, false)}
        ${row('Add to Home screen', SVG.phone, true)}
      </div>`),
    step(3, 'Tap Install (or Add) to confirm.', `<div class="a2hs-sheet">
        ${appRow(host)}
        <div class="a2hs-confirm"><span>Cancel</span><b>${SVG.download}Install</b></div>
      </div>`, { last: true }),
  ].join('\n');
}

/**
 * In-app browsers (Facebook, Instagram, TikTok, WeChat…) can install nothing:
 * they are a WKWebView or an Android WebView, which have no install API and no
 * Add to Home Screen anywhere in their menus. Teaching Safari's or Chrome's
 * taps there would be teaching taps that do not exist, so this flow teaches the
 * only move that helps — leave for the real browser first. Paid social is where
 * most of these visits come from, which is exactly why it cannot be a dead end.
 */
function webviewSteps() {
  return [
    step(1, 'Tap the ••• menu (top-right of this in-app browser).', `<div class="a2hs-bar">
        <span class="a2hs-url a2hs-url-wide">ember.space</span>
        <span class="a2hs-tap a2hs-tap-ios">${SVG.dotsH}</span>
      </div>`),
    step(2, 'Choose Open in Safari — or Open in browser / Open in Chrome.', `<div class="a2hs-sheet">
        ${row('Open in Safari', SVG.iosShare, true)}
        ${row('Copy link', SVG.book, false)}
        ${row('Share…', SVG.share2, false)}
      </div>`),
    step(3, 'Tap Install again there. This app’s browser cannot add anything to your Home Screen.', '', { last: true }),
    `<button class="a2hs-copy" type="button" data-a2hs-copy>Copy link instead</button>`,
  ].join('\n');
}

/**
 * Why the person is reading instructions instead of having tapped once.
 *
 * The guide is ONLY ever opened when the one-tap install was unavailable —
 * promptInstallOrGuide fires Chromium's prompt whenever it has one — so
 * reaching this modal is itself the answer, and each platform's line can be
 * stated plainly rather than guessed at:
 *
 *   android — Chromium had no prompt for this page. Usually its engagement
 *             heuristic (it wants a real visit first) or an install it already
 *             offered and had refused; sometimes a browser that has none at all
 *             (Samsung Internet, Firefox). All of them still install from the
 *             menu, which is what the steps below show — so the line names the
 *             limitation without pretending to diagnose it.
 *   ios     — no install API exists on iOS at all, in any browser. This is not
 *             a temporary condition and should not be described as one.
 *   webview — an in-app browser installs nothing, and its own steps say so.
 */
const GUIDE_NOTES = {
  android: 'Your browser didn’t offer one-tap install here — Ember may already be installed, or Chrome wants a longer visit first. These taps do the same thing.',
  ios: 'iPhone has no one-tap install. Safari only offers this manual path — it takes three taps.',
  webview: '',
};

/**
 * The whole modal for one platform — overlay, card, header, steps, "Got it".
 * Anything that closes it carries `data-a2hs-close`, and the overlay itself
 * carries `data-a2hs-overlay`, so both the React wrapper and the inline script
 * on a server-rendered page bind the same two hooks instead of two dialects.
 *
 * `os` is 'ios' or 'android'; anything else is treated as iOS, which is what
 * the app bundle's guides already do (iOS is the only platform with no API,
 * so it is the safe default when detection is uncertain).
 */
export function installGuideHtml({ os = 'ios', host = 'ember.space' } = {}) {
  const webview = os === 'webview';
  const android = os === 'android';
  const titleId = `a2hs-title-${webview ? 'webview' : (android ? 'android' : 'ios')}`;
  const title = webview ? 'Open Ember in your browser first' : 'Add Ember to your Home Screen';
  const sub = webview ? 'Two taps to leave this in-app browser' : `${android ? 'In Chrome' : 'In Safari'} — 3 quick taps`;
  const note = GUIDE_NOTES[webview ? 'webview' : (android ? 'android' : 'ios')] || '';
  return `<div class="a2hs-overlay" data-a2hs-overlay role="dialog" aria-modal="true" aria-labelledby="${titleId}">
  <div class="a2hs-card">
    <button class="a2hs-x" type="button" data-a2hs-close aria-label="Close">${SVG.close}</button>
    <div class="a2hs-head">
      <img class="a2hs-icon" src="/icon-192.png" alt="Ember" decoding="async">
      <div class="a2hs-head-text">
        <h3 class="a2hs-title" id="${titleId}">${title}</h3>
        <p class="a2hs-sub">${sub}</p>
      </div>
    </div>
    ${note ? `<p class="a2hs-note">${note}</p>` : ''}
    <div class="a2hs-steps">
      ${webview ? webviewSteps() : (android ? androidSteps(host) : iosSteps(host))}
    </div>
    <button class="a2hs-done" type="button" data-a2hs-close>Got it</button>
  </div>
</div>`;
}

/**
 * The guide's skin. Self-contained and prefixed `a2hs-`: it is inlined into a
 * server-rendered story page next to that page's own skin CSS, and imported by
 * the marketing bundle, so it can neither depend on a design system nor leak
 * into one. Light card on every surface (including the dark homepage footer and
 * the dark story skins) — it is a picture of the OS, and the OS sheets are light.
 */
export const INSTALL_GUIDE_CSS = `
.a2hs-overlay{position:fixed;inset:0;z-index:100;display:flex;align-items:center;justify-content:center;padding:16px;background:rgba(0,0,0,.42);-webkit-backdrop-filter:blur(4px);backdrop-filter:blur(4px);font-family:ui-sans-serif,system-ui,-apple-system,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;-webkit-font-smoothing:antialiased;}
.a2hs-card{position:relative;width:100%;max-width:360px;max-height:90vh;overflow-y:auto;padding:28px 24px 24px;border-radius:24px;background:#FBF9F4;color:#12100e;box-shadow:0 25px 50px -12px rgba(0,0,0,.35);text-align:left;line-height:1.5;}
.a2hs-x{position:absolute;top:14px;right:14px;width:28px;height:28px;display:flex;align-items:center;justify-content:center;padding:0;border:0;border-radius:50%;background:transparent;color:#a3a3a3;cursor:pointer;transition:color .15s,background .15s;}
.a2hs-x:hover{color:#404040;background:#f0efec;}
.a2hs-x svg{width:16px;height:16px;}
.a2hs-head{display:flex;align-items:center;gap:12px;margin-bottom:20px;-webkit-user-select:none;user-select:none;}
.a2hs-icon{width:44px;height:44px;flex:0 0 auto;border-radius:12px;border:1px solid rgba(0,0,0,.08);}
.a2hs-head-text{display:flex;flex-direction:column;min-width:0;}
.a2hs-title{margin:0;font-family:"Palatino Linotype","Book Antiqua",Palatino,"Iowan Old Style",Georgia,serif;font-size:15px;font-weight:500;line-height:1.35;color:#12100e;}
.a2hs-sub{margin:2px 0 0;font-family:ui-monospace,"SF Mono",SFMono-Regular,Menlo,Consolas,monospace;font-size:11px;letter-spacing:.02em;color:#a3a3a3;}
.a2hs-note{margin:-8px 0 16px;padding:9px 12px;border-radius:12px;background:rgba(244,114,12,.08);color:#6b4a2f;font-size:12px;line-height:1.45;}
.a2hs-steps{display:flex;flex-direction:column;}
.a2hs-step{display:flex;gap:14px;}
.a2hs-rail{display:flex;flex-direction:column;align-items:center;flex:0 0 auto;}
.a2hs-badge{width:20px;height:20px;margin-top:-1px;display:flex;align-items:center;justify-content:center;border-radius:50%;background:#12100e;color:#fff;font-size:10px;font-weight:700;line-height:1;}
.a2hs-line{width:1px;flex:1;margin:4px 0;background:#e5e5e5;}
.a2hs-step-body{display:flex;flex-direction:column;gap:8px;flex:1;min-width:0;padding-bottom:20px;}
.a2hs-cap{margin:0;font-size:13px;line-height:18px;color:#0a0908;}
.a2hs-mock{font-size:0;}
.a2hs-bar{display:flex;align-items:center;gap:8px;padding:8px 12px;border:1px solid rgba(0,0,0,.09);border-radius:16px;background:#f7f7f8;}
.a2hs-bar-ico{flex:0 0 auto;color:#c9c9c9;}
.a2hs-bar-ico svg{width:16px;height:16px;display:block;}
.a2hs-url{flex:1;min-width:0;display:flex;align-items:center;justify-content:center;gap:6px;padding:4px 10px;border-radius:999px;background:#fff;color:#737373;font-size:11px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.a2hs-url-wide{justify-content:flex-start;}
.a2hs-url i{flex:0 0 auto;display:block;color:#a3a3a3;}
.a2hs-url i svg{width:11px;height:11px;display:block;}
.a2hs-tap{position:relative;flex:0 0 auto;width:28px;height:28px;display:flex;align-items:center;justify-content:center;border-radius:50%;}
.a2hs-tap svg{width:16px;height:16px;display:block;}
.a2hs-tap::after{content:"";position:absolute;inset:-4px;border-radius:50%;border:2px solid currentColor;animation:a2hs-pulse 1.8s ease-in-out infinite;}
.a2hs-tap-ios{background:#e6f0ff;color:#007AFF;}
.a2hs-tap-android{background:#e6f0ff;color:#1a73e8;}
@keyframes a2hs-pulse{0%,100%{opacity:1;}50%{opacity:.35;}}
@media (prefers-reduced-motion: reduce){.a2hs-tap::after{animation:none;}}
.a2hs-sheet{border:1px solid rgba(0,0,0,.09);border-radius:16px;background:#f5f5f7;overflow:hidden;}
.a2hs-row{display:flex;align-items:center;gap:12px;padding:10px 14px;border-top:1px solid rgba(0,0,0,.06);}
.a2hs-sheet > .a2hs-row:first-child{border-top:0;}
.a2hs-row.is-active{background:#fff;}
.a2hs-row-ico{flex:0 0 auto;display:block;color:#12100e;}
.a2hs-row-ico svg{width:18px;height:18px;display:block;}
.a2hs-row-label{font-size:14px;color:#12100e;}
.a2hs-row.is-active .a2hs-row-label{font-weight:500;}
.a2hs-app-row{display:flex;align-items:center;gap:12px;padding:10px 14px;background:rgba(255,255,255,.7);border-bottom:1px solid rgba(0,0,0,.07);}
.a2hs-app-thumb{width:36px;height:36px;flex:0 0 auto;border-radius:9px;border:1px solid rgba(0,0,0,.08);}
.a2hs-app-text{display:flex;flex-direction:column;min-width:0;}
.a2hs-app-text b{font-size:13px;font-weight:600;line-height:1.25;color:#12100e;}
.a2hs-app-text i{font-style:normal;font-size:10px;color:#a3a3a3;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.a2hs-confirm{display:flex;align-items:center;justify-content:flex-end;gap:16px;padding:10px 16px;border-top:1px solid rgba(0,0,0,.06);}
.a2hs-confirm span{font-size:13px;color:#a3a3a3;}
.a2hs-confirm b{display:inline-flex;align-items:center;gap:6px;font-size:13px;font-weight:600;color:#1a73e8;}
.a2hs-confirm b svg{width:14px;height:14px;display:block;}
.a2hs-done{width:100%;margin-top:4px;padding:10px 16px;border:0;border-radius:999px;background:#F4720C;color:#fff;font-size:14px;font-weight:700;font-family:inherit;cursor:pointer;transition:background .15s;}
.a2hs-done:hover{background:#D9640A;}
.a2hs-copy{width:100%;margin-top:10px;padding:10px 16px;border:1px solid rgba(0,0,0,.1);border-radius:999px;background:#fff;color:#3f352e;font-size:14px;font-weight:650;font-family:inherit;cursor:pointer;}
.a2hs-copy:hover{background:#faf8f5;}
`;

/* ------------------------------------------------------------------------ *
 * Runtime. The guide is a plain HTML string with two hooks in it
 * (`data-a2hs-close` on the X and "Got it", `data-a2hs-overlay` on the
 * backdrop), so opening it is DOM work, not framework work — which is why the
 * React button below can share this exact behavior without a portal.
 * ------------------------------------------------------------------------ */

/**
 * Telemetry. iOS has no install API and Chromium withholds its prompt on its
 * own terms, so nothing about this feature is observable unless the click, the
 * way it ended, and the reason it ended that way are all written down —
 * install.clicked / install.resolved / install.completed, declared in
 * analytics/metadata/registry.js.
 *
 * Posted straight to the one client-event endpoint rather than through
 * shared-web/track.ts, because the story page carries no bundle to import it
 * from and both surfaces must land the same rows. keepalive, since the native
 * prompt and the share sheet both take the page away mid-request.
 */
export function reportInstall(type, props = {}, win = typeof window !== 'undefined' ? window : undefined) {
  if (!win || typeof win.fetch !== 'function') return;
  try {
    win.fetch('/api/events', {
      method: 'POST',
      credentials: 'same-origin',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        events: [{ type, client_ts: Date.now(), path: win.location.pathname, props }],
      }),
    }).catch(() => {});
  } catch {
    // Telemetry never breaks the thing it is measuring.
  }
}

/**
 * install.completed — the two ways this is ever knowable. `appinstalled` is
 * Chromium's; a standalone launch is the only trace iOS leaves, reported once
 * per session so a week of daily opens is not a week of installs.
 */
export function reportInstallLaunch({ source = '', os = '', win = typeof window !== 'undefined' ? window : undefined } = {}) {
  if (!win || !isStandaloneDisplay(win)) return;
  try {
    if (win.sessionStorage.getItem('ori_a2hs_launch') === '1') return;
    win.sessionStorage.setItem('ori_a2hs_launch', '1');
  } catch {
    // A browser refusing storage still gets counted; a duplicate is better
    // than a blind spot.
  }
  reportInstall('install.completed', {
    ...(source ? { source } : {}),
    ...(os ? { os } : {}),
    detected: 'standalone_launch',
  }, win);
}

/**
 * Both install.completed signals, armed once per bundle start on ANY page
 * (2026-09-06). Until then the Add to Home Screen component reported them,
 * so an installed person who opened the app and never reached the page that
 * mounts the button was never counted as installed. `source` (which button
 * the install came from) is not knowable at launch and is left off.
 */
export function initInstallCompletion({ os = '', win = typeof window !== 'undefined' ? window : undefined } = {}) {
  if (!win) return;
  reportInstallLaunch({ os, win });
  try {
    win.addEventListener('appinstalled', () => {
      reportInstall('install.completed', { ...(os ? { os } : {}), detected: 'appinstalled' }, win);
    }, { once: true });
  } catch {
    // Telemetry never breaks the thing it is measuring.
  }
}

/** True when the page is already running from the home-screen icon. */
export function isStandaloneDisplay(win = typeof window !== 'undefined' ? window : undefined) {
  if (!win) return false;
  if (win.matchMedia && win.matchMedia('(display-mode: standalone)').matches) return true;
  return win.navigator && win.navigator.standalone === true;
}

/**
 * "Installed, but browsing in a tab" — the one installed state standalone
 * detection cannot see. Android Chromium answers it through
 * getInstalledRelatedApps(), which matches the manifest's own
 * `related_applications` webapp entry (public/manifest.json — where
 * `prefer_related_applications` must stay absent, or Chromium stops offering
 * the install prompt at all). Resolves false wherever the API does not exist
 * (all of iOS, Firefox, desktop Safari) or refuses to answer, so a caller can
 * only ever hide MORE with it, never offer less.
 */
export async function isInstalledAsRelatedApp(win = typeof window !== 'undefined' ? window : undefined) {
  const nav = win && win.navigator;
  if (!nav || typeof nav.getInstalledRelatedApps !== 'function') return false;
  try {
    const apps = await nav.getInstalledRelatedApps();
    return Array.isArray(apps) && apps.length > 0;
  } catch {
    return false;
  }
}

/** Inject the guide's skin once per document. */
export function ensureInstallGuideStyles(doc = typeof document !== 'undefined' ? document : undefined) {
  if (!doc || doc.getElementById('a2hs-styles')) return;
  const style = doc.createElement('style');
  style.id = 'a2hs-styles';
  style.textContent = INSTALL_GUIDE_CSS;
  doc.head.appendChild(style);
}

/**
 * Open the platform guide over the current page. Returns a close function; the
 * modal also closes on the X, "Got it", the backdrop and Escape, and restores
 * the scroll lock it took.
 */
export function openInstallGuide({ os = 'ios', host = '', doc = typeof document !== 'undefined' ? document : undefined } = {}) {
  if (!doc) return () => {};
  ensureInstallGuideStyles(doc);
  const holder = doc.createElement('div');
  holder.innerHTML = installGuideHtml({ os, host: host || (doc.location && doc.location.host) || 'ember.space' });
  const overlay = holder.firstElementChild;
  if (!overlay) return () => {};

  const previousOverflow = doc.body.style.overflow;
  const close = () => {
    doc.removeEventListener('keydown', onKey);
    overlay.remove();
    doc.body.style.overflow = previousOverflow;
  };
  function onKey(event) { if (event.key === 'Escape') close(); }

  overlay.addEventListener('click', (event) => {
    const target = event.target;
    if (!target || !target.closest) return;
    const copy = target.closest('[data-a2hs-copy]');
    if (copy) {
      const url = doc.location && doc.location.href;
      if (url && doc.defaultView && doc.defaultView.navigator.clipboard) {
        doc.defaultView.navigator.clipboard.writeText(url).then(() => { copy.textContent = 'Link copied'; }).catch(() => {});
      }
      return;
    }
    if (target.closest('[data-a2hs-close]') || !target.closest('.a2hs-card')) close();
  });
  doc.addEventListener('keydown', onKey);
  doc.body.style.overflow = 'hidden';
  doc.body.appendChild(overlay);
  return close;
}

/**
 * The whole click: fire Chromium's captured prompt when there is one, and fall
 * back to the platform guide when there is not (always, on iOS). The prompt is
 * cleared after use because Chromium allows one prompt() per event — keeping it
 * would leave a button that silently does nothing the second time.
 */
export async function promptInstallOrGuide({ os = 'ios', host = '', source = '', win = typeof window !== 'undefined' ? window : undefined } = {}) {
  const prompt = win && win[INSTALL_PROMPT_KEY];
  const path = prompt ? 'native' : 'guide';
  reportInstall('install.clicked', { source, os, path }, win);

  if (prompt) {
    try {
      await prompt.prompt();
      const choice = await prompt.userChoice;
      win[INSTALL_PROMPT_KEY] = undefined;
      const outcome = choice && choice.outcome === 'accepted' ? 'accepted' : 'dismissed';
      reportInstall('install.resolved', { source, os, path: 'native', outcome, reason: '' }, win);
      return 'native';
    } catch (error) {
      // A prompt that refuses to open still leaves the manual path, which
      // works — but WHY it refused is the whole point of logging this.
      reportInstall('install.resolved', {
        source, os, path: 'guide', outcome: 'guide_shown',
        reason: `prompt_failed:${(error && error.name) || 'Error'}`,
      }, win);
      openInstallGuide({ os, host, doc: win && win.document });
      return 'guide';
    }
  }

  reportInstall('install.resolved', {
    source, os, path: 'guide', outcome: 'guide_shown',
    reason: os === 'webview' ? 'in_app_webview' : (os === 'ios' ? 'os_ios' : 'no_prompt'),
  }, win);
  openInstallGuide({ os, host, doc: win && win.document });
  return 'guide';
}

